import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import logo from '../assets/logo.png';

function Login() {
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [needsVerification, setNeedsVerification] = useState(false);
    const [mfaRequired, setMfaRequired] = useState(false);
    const [vCode, setVCode] = useState('');
    const [resending, setResending] = useState(false);
    const [resendMsg, setResendMsg] = useState('');
    const [isVerified, setIsVerified] = useState(false);
    
    // Forgot Password Flow
    const [resetFlow, setResetFlow] = useState('none'); // none, request, reset, success
    const [resetEmail, setResetEmail] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [newPassword, setNewPassword] = useState('');

    const { login, verifyEmail, resendVerification, verifyAdminMfa, forgotPassword, resetPassword } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await login(email, password);
            navigate('/home');
        } catch (err) {
            if (err.response?.data?.needs_verification) {
                setNeedsVerification(true);
            } else if (err.response?.data?.mfa_required) {
                setMfaRequired(true);
            } else {
                setError(err.response?.data?.error || 'Failed to login');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (vCode.length !== 6) return setError('Please enter a 6-digit code');
        setError('');
        setLoading(true);
        try {
            if (mfaRequired) {
                await verifyAdminMfa(email, vCode);
            } else {
                await verifyEmail(email, vCode);
            }
            setIsVerified(true);
            setTimeout(() => navigate('/home'), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Invalid verification code');
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setResendMsg('');
        try {
            await resendVerification(email);
            setResendMsg('Verification code resent!');
        } catch (err) {
            setError('Failed to resend code');
        } finally {
            setResending(false);
        }
    };

    const handleRequestReset = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await forgotPassword(resetEmail);
            setResetFlow('reset');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to send reset code');
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        if (resetCode.length !== 6) return setError('Please enter a 6-digit code');
        if (newPassword.length < 8) return setError('Password must be at least 8 characters');
        
        setError('');
        setLoading(true);
        try {
            await resetPassword(resetEmail, resetCode, newPassword);
            setResetFlow('success');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to reset password');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={styles.container}>
            {/* Header Section */}
            <div style={styles.header}>
                <div style={styles.welcomeLine}>
                    <div style={styles.line} />
                    <span style={styles.welcomeText}>WELCOME TO</span>
                    <div style={styles.line} />
                </div>
                <img src={logo} alt="The Copper Road" style={styles.logo} />
                <div style={styles.tagline}>Every Road Leads to Something Worth Tasting</div>
                <div style={styles.goldDiamond}>
                    <div style={styles.diamondInner} />
                </div>
            </div>

            {/* Auth Card */}
            <div style={styles.card}>
                {(needsVerification || mfaRequired) ? (
                    isVerified ? (
                        <div style={styles.successContainer}>
                            <div style={{...styles.successIcon, borderColor: '#34d399', color: '#34d399', width: 60, height: 60, fontSize: 30}}>✓</div>
                            <h2 style={styles.cardHeader}>Success!</h2>
                            <p style={styles.successText}>
                                Verification complete. Redirecting you...
                            </p>
                            <div style={styles.loaderLine}>
                                <div style={styles.loaderProgress} />
                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ ...styles.successIcon, width: 60, height: 60, fontSize: 30 }}>✉</div>
                            <h2 style={styles.cardHeader}>{mfaRequired ? 'Admin Security' : 'Verify Your Email'}</h2>
                            <p style={{ ...styles.footer, marginTop: -10, marginBottom: 24, textAlign: 'left' }}>
                                {mfaRequired 
                                    ? <>For security, an admin code was sent to <strong style={{color:'#1c1c1c'}}>{email}</strong>.</>
                                    : <>Your email <strong>{email}</strong> is not verified yet. Please enter the 6-digit code we sent you.</>
                                }
                            </p>

                            {error && <div style={styles.error}>{error}</div>}
                            {resendMsg && <div style={{ ...styles.error, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>{resendMsg}</div>}

                            <form onSubmit={handleVerify} style={styles.form}>
                                <div style={styles.group}>
                                    <label style={styles.label}>6-DIGIT CODE</label>
                                    <input
                                        type="text"
                                        maxLength="6"
                                        placeholder="000000"
                                        value={vCode}
                                        onChange={(e) => setVCode(e.target.value.replace(/\D/g, ''))}
                                        style={{ ...styles.input, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 'bold' }}
                                        required
                                    />
                                </div>

                                <button type="submit" style={styles.button} disabled={loading}>
                                    {loading ? 'Verifying...' : 'Verify & Continue →'}
                                </button>
                            </form>

                            <p style={styles.footer}>
                                Didn't get the code? <br />
                                <button
                                    onClick={handleResend}
                                    disabled={resending}
                                    style={{ background: 'none', border: 'none', color: '#b8753d', fontWeight: 'bold', cursor: 'pointer', marginTop: 8 }}
                                >
                                    {resending ? 'Sending...' : 'Resend Verification Email'}
                                </button>
                            </p>
                            
                            <button 
                                onClick={() => { setNeedsVerification(false); setMfaRequired(false); }}
                                style={{ background: 'none', border: 'none', color: '#a1a1aa', fontSize: 13, marginTop: 16, cursor: 'pointer' }}
                            >
                                ← Back to Sign In
                            </button>
                        </div>
                    )
                ) : resetFlow === 'request' ? (
                    <div style={{ textAlign: 'center' }}>
                        <h2 style={styles.cardHeader}>Reset Password</h2>
                        <p style={{ ...styles.footer, marginTop: -10, marginBottom: 24, textAlign: 'left' }}>
                            Enter your email address and we'll send you a code to reset your password.
                        </p>

                        {error && <div style={styles.error}>{error}</div>}

                        <form onSubmit={handleRequestReset} style={styles.form}>
                            <div style={styles.group}>
                                <label style={styles.label}>EMAIL</label>
                                <input
                                    type="email"
                                    value={resetEmail}
                                    onChange={(e) => setResetEmail(e.target.value)}
                                    style={styles.input}
                                    required
                                />
                            </div>

                            <button type="submit" style={styles.button} disabled={loading}>
                                {loading ? 'Sending...' : 'Send Reset Code →'}
                            </button>
                        </form>

                        <button 
                            onClick={() => setResetFlow('none')}
                            style={{ background: 'none', border: 'none', color: '#a1a1aa', fontSize: 13, marginTop: 20, cursor: 'pointer' }}
                        >
                            ← Back to Sign In
                        </button>
                    </div>
                ) : resetFlow === 'reset' ? (
                    <div style={{ textAlign: 'center' }}>
                        <h2 style={styles.cardHeader}>Set New Password</h2>
                        <p style={{ ...styles.footer, marginTop: -10, marginBottom: 24, textAlign: 'left' }}>
                            Enter the 6-digit code sent to <strong>{resetEmail}</strong> and your new password.
                        </p>

                        {error && <div style={styles.error}>{error}</div>}

                        <form onSubmit={handleResetPassword} style={styles.form}>
                            <div style={styles.group}>
                                <label style={styles.label}>6-DIGIT CODE</label>
                                <input
                                    type="text"
                                    maxLength="6"
                                    placeholder="000000"
                                    value={resetCode}
                                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, ''))}
                                    style={{ ...styles.input, textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 'bold' }}
                                    required
                                />
                            </div>
                            <div style={styles.group}>
                                <label style={styles.label}>NEW PASSWORD</label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    style={styles.input}
                                    placeholder="At least 8 characters"
                                    required
                                />
                            </div>

                            <button type="submit" style={styles.button} disabled={loading}>
                                {loading ? 'Resetting...' : 'Update Password →'}
                            </button>
                        </form>

                        <button 
                            onClick={() => setResetFlow('request')}
                            style={{ background: 'none', border: 'none', color: '#a1a1aa', fontSize: 13, marginTop: 20, cursor: 'pointer' }}
                        >
                            ← Resend Code
                        </button>
                    </div>
                ) : resetFlow === 'success' ? (
                    <div style={styles.successContainer}>
                        <div style={{...styles.successIcon, borderColor: '#34d399', color: '#34d399', width: 60, height: 60, fontSize: 30}}>✓</div>
                        <h2 style={styles.cardHeader}>Password Updated!</h2>
                        <p style={styles.successText}>
                            Your password has been reset successfully. You can now sign in.
                        </p>
                        <button onClick={() => setResetFlow('none')} style={styles.button}>
                            Sign In Now →
                        </button>
                    </div>
                ) : (
                    <>
                        <h2 style={styles.cardHeader}>Sign In</h2>

                        {location.state?.message && (
                            <div style={{ ...styles.error, backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                                ✓ {location.state.message}
                            </div>
                        )}

                        {error && <div style={styles.error}>{error}</div>}

                        <form onSubmit={handleSubmit} style={styles.form}>
                            <div style={styles.group}>
                                <label style={styles.label}>EMAIL</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={styles.input}
                                    autoComplete="email"
                                    required
                                />
                            </div>
                            <div style={styles.group}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <label style={styles.label}>PASSWORD</label>
                                    <button 
                                        type="button" 
                                        onClick={() => setResetFlow('request')}
                                        style={{ ...styles.label, border: 'none', background: 'none', color: '#b8753d', cursor: 'pointer', padding: 0 }}
                                    >
                                        FORGOT?
                                    </button>
                                </div>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    style={styles.input}
                                    autoComplete="current-password"
                                    required
                                />
                            </div>

                            <button type="submit" style={styles.button} disabled={loading}>
                                {loading ? 'Sign In...' : 'Sign In →'}
                            </button>
                        </form>
                    </>
                )}



                <div style={styles.divider}>
                    <div style={styles.dividerLine} />
                    <span style={styles.dividerText}>OR</span>
                    <div style={styles.dividerLine} />
                </div>

                <div style={styles.socialGrid}>
                    <button 
                        style={styles.socialBtn}
                        onClick={() => window.location.href = 'https://copperroadexperiences.com/copper-road-laravel/public/api/auth/google/redirect'}
                    >
                        <span style={styles.socialIcon}>G</span> Sign in with Google
                    </button>
                    <button 
                        style={styles.socialBtn}
                        onClick={() => window.location.href = 'https://copperroadexperiences.com/copper-road-laravel/public/api/auth/apple/redirect'}
                    >
                        <span style={styles.socialIcon}></span> Sign in with Apple
                    </button>
                </div>
            </div>

            <div style={styles.bottomFooter}>
                New? <Link to="/register" style={styles.bottomLink}>Create a free account →</Link>
            </div>
        </div>
    );
}

const styles = {
    container: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        minHeight: '100vh', background: '#0a0f14', padding: '60px 24px',
        fontFamily: 'Inter, system-ui, sans-serif'
    },
    header: { textAlign: 'center', marginBottom: 50, width: '100%', maxWidth: 400 },
    logo: { width: '220px', height: 'auto', marginBottom: 12 },
    tagline: { color: '#8a6d3b', fontSize: 13, fontFamily: 'Georgia, serif', fontStyle: 'italic', marginBottom: 5, letterSpacing: 0.5 },
    welcomeLine: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 15, marginBottom: 20 },
    line: { height: 1.5, background: '#8a6d3b', width: 30 },
    welcomeText: { color: '#8a6d3b', fontSize: 11, fontWeight: 'bold', letterSpacing: 2 },
    goldDiamond: {
        marginTop: 25, display: 'flex', justifyContent: 'center'
    },
    diamondInner: {
        width: 32, height: 32, background: '#f59e0b', transform: 'rotate(45deg)', borderRadius: 4
    },

    card: {
        width: '100%', maxWidth: '380px', padding: '32px', backgroundColor: 'white',
        borderRadius: '30px', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', textAlign: 'left'
    },
    cardHeader: {
        fontSize: '28px', color: '#1c1c1c', fontFamily: 'Georgia, serif',
        fontWeight: 'bold', marginBottom: '24px'
    },
    error: {
        backgroundColor: '#fff1f2', color: '#e11d48', padding: '12px',
        borderRadius: '8px', fontSize: '13px', marginBottom: '20px', fontWeight: '500'
    },
    form: { display: 'flex', flexDirection: 'column', gap: 20 },
    group: { display: 'flex', flexDirection: 'column', gap: 8 },
    label: { fontSize: '10px', fontWeight: 'bold', color: '#a1a1aa', letterSpacing: 1 },
    input: {
        width: '100%', padding: '14px 16px', border: '1.2px solid #e4e4e7',
        borderRadius: '14px', fontSize: '15px', color: '#1c1c1c', outline: 'none'
    },
    button: {
        width: '100%', padding: '16px',
        background: 'linear-gradient(135deg, #b8753d 0%, #8d5d36 100%)',
        color: 'white', border: 'none', borderRadius: '40px',
        fontWeight: 'bold', fontSize: '16px', cursor: 'pointer',
        boxShadow: '0 8px 20px rgba(184,117,61,0.4)', marginTop: 10
    },
    footer: { textAlign: 'center', marginTop: '24px', fontSize: '14px', color: '#71717a' },
    signLink: { color: '#b8753d', textDecoration: 'none', fontWeight: 'bold' },

    bottomFooter: { marginTop: 'auto', paddingTop: 40, color: '#71717a', fontSize: 13 },
    bottomLink: { color: '#f59e0b', textDecoration: 'none', fontWeight: '500', marginLeft: 4 },

    divider: { display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0' },
    dividerLine: { flex: 1, height: 1, background: '#f0f0f0' },
    dividerText: { color: '#a1a1aa', fontSize: 11, fontWeight: 'bold' },

    socialGrid: { display: 'flex', flexDirection: 'column', gap: 12 },
    socialBtn: {
        width: '100%', padding: '12px', background: '#fff', border: '1.2px solid #e4e4e7',
        borderRadius: '14px', fontSize: '14px', color: '#1c1c1c', fontWeight: '600',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10
    },
    socialIcon: { fontSize: 16, fontWeight: 'bold' },

    // Success Styles
    successContainer: { textAlign: 'center', padding: '20px 0' },
    successIcon: {
        width: 80, height: 80, background: '#ecfdf5', color: '#10b981',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 40, margin: '0 auto 24px', border: '4px solid #d1fae5'
    },
    successText: { color: '#71717a', fontSize: '16px', lineHeight: 1.6, marginBottom: 32 },
    loaderLine: { width: '100%', height: 4, background: '#f4f4f5', borderRadius: 2, overflow: 'hidden', position: 'relative' },
    loaderProgress: {
        position: 'absolute', top: 0, left: 0, height: '100%', background: '#b8753d', width: '100%',
        animation: 'progress 3s linear forwards'
    }
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes progress {
    0% { width: 0%; }
    100% { width: 100%; }
  }
`;
document.head.appendChild(styleSheet);


export default Login;
